import React, { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import FullCalendar from "@fullcalendar/react" // must go before plugins
import dayGridPlugin from "@fullcalendar/daygrid" // a plugin!
import timeGridPlugin from "@fullcalendar/timegrid" // a plugin!
import moment from "moment"
import { createPopper } from "@popperjs/core"

import { getStudentClasses } from "store/user/actions"

import Text from "components/text"
import Panel from "components/panel"
import "./index.scss"

const ClassCalendar = () => {
  const dispatch = useDispatch()
  const [activeEvent, setActiveEvent] = useState(false)
  const [toScroll, setToScroll] = useState(false)
  const classCalendar = useSelector((state) => state.userReducer.classCalendar)
  const isLoadingClasses = useSelector(
    (state) => state.userReducer.isLoadingClasses
  )
  const calendarErrors = useSelector((state) => state.userReducer.errors)

  const handleEventDidMount = (info) => {
    const popover = document.querySelector(
      `#class-event-popover-${info?.event?.id}`
    )
    const boundary = document.querySelector(
      ".fc-scroller.fc-scroller-liquid-absolute"
    )

    createPopper(info.el, popover, {
      placement: "auto",
      modifiers: [
        {
          name: "preventOverflow",
          options: {
            boundary: boundary,
          },
        },
      ],
    })
  }

  const handleChangeView = () => {
    setActiveEvent(false)
  }

  const handleChangeDate = (data) => {
    const params = {
      date_start: moment(data.start).format("YYYY-MM-DD"),
      date_end: moment(data.end).subtract(1, "days").format("YYYY-MM-DD"),
    }

    const queryString = new URLSearchParams(params).toString()

    setActiveEvent(false)
    dispatch(getStudentClasses(queryString))
  }

  const viewEvent = (eventId) => {
    if (activeEvent === eventId) {
      setActiveEvent(false)
    } else {
      setActiveEvent(eventId)
    }
    if (toScroll) {
      window.scrollBy(0, 1)
    } else {
      window.scrollBy(0, -1)
    }
    setToScroll(!toScroll)
  }

  const renderEventPopovers = (events = []) => {
    if (events && events.length > 0) {
      return events.map((event = {}, idx) => {
        const { title, instructor, units, class_link } = event
        const eventId = event.id + ""
        const startTime = event.start
          ? moment(event.start).format("h:mm A")
          : false
        const endTime = event.end ? moment(event.end).format("h:mm A") : false

        return (
          <div
            key={`class-event-popover-${eventId}`}
            id={`class-event-popover-${eventId}`}
            className={`class-event-popover ${
              activeEvent === eventId ? "show" : ""
            }`}
            role="tooltip"
          >
            <p className="title">{title}</p>
            <hr />
            <p className="instructor">
              {instructor}
              {units ? ` — ${units} Units` : ""}
            </p>
            <p className="schedule">
              {startTime}
              {endTime && ` — ${endTime}`}
            </p>
            {class_link && (
              <a href={class_link} target="blank" className="class-link">
                Class Link
              </a>
            )}
            <div data-popper-arrow></div>
          </div>
        )
      })
    }
  }

  const renderEvent = (data) => {
    const { view = {}, isToday, isFuture, event } = data || {}

    const classes = []
    const monthlyView = view.type === "dayGridMonth"

    if (isToday) {
      classes.push("today")
    } else if (isFuture) {
      classes.push("future")
    } else {
      classes.push("past")
    }

    if (monthlyView) {
      classes.push("monthly")
    } else if (view.type === "timeGridDay") {
      classes.push("daily")
    } else {
      classes.push("weekly")
    }

    const startTime = event.start ? moment(event.start).format("h:mm A") : false
    const endTime = event.end ? moment(event.end).format("h:mm A") : false

    const eventId = event.id

    return (
      <>
        <div
          className={`custom-event ${classes.join(" ")}`}
          id={`class-event-${eventId}`}
          onClick={() => viewEvent(eventId)}
        >
          <div className="title">{event.title}</div>
          {monthlyView && (
            <div className="time">
              {startTime}
              {endTime && ` — ${endTime}`}
            </div>
          )}
        </div>
      </>
    )
  }

  const renderSlotLabel = (data) => {
    const { date } = data || {}
    return (
      <div className="time-slot">
        {moment(date).format("h A").toUpperCase()}
      </div>
    )
  }

  const renderDayCell = (data) => {
    const { view = {}, date, isToday, isFuture } = data || {}

    const monthlyView = view.type === "dayGridMonth"

    const classes = []
    if (isToday) {
      classes.push("today")
    } else if (isFuture) {
      classes.push("future")
    } else {
      classes.push("past")
    }

    return monthlyView ? (
      <div className={classes.join(" ")}>
        {moment(date).format("DD").toUpperCase()}
      </div>
    ) : (
      ""
    )
  }

  const renderDayHeader = (data) => {
    const { view = {}, date, isToday, isFuture } = data || {}

    const classes = []
    if (isToday) {
      classes.push("today")
    } else if (isFuture) {
      classes.push("future")
    } else {
      classes.push("past")
    }

    return (
      <div className={`day-header ${classes.join(" ")}`}>
        <p>{moment(date).format("ddd").toUpperCase()}</p>
        {view?.type !== "dayGridMonth" && <h3>{moment(date).format("DD")}</h3>}
      </div>
    )
  }

  return (
    <section id="class-calendar">
      <Panel pad="40" grid>
        <Text type="headline" text="My Classes" />
        <p>{calendarErrors}</p>
        <div className="calendar-container">
          <h1 className="calendar-title">Calendar</h1>
          <p className="calendar-subtitle">
            Here all your planned events. You will find information for each
            event.
          </p>
          <br />
          <FullCalendar
            titleFormat={{ year: "numeric", month: "short" }}
            allDaySlot={false}
            plugins={[dayGridPlugin, timeGridPlugin]}
            nowIndicator={true}
            dayMinWidth="100px"
            initialView="timeGridWeek"
            eventDisplay="block"
            eventBackgroundColor="rgba(0,0,0,0)"
            eventBorderColor="transparent"
            eventMinHeight={20}
            dayMaxEvents={2}
            events={isLoadingClasses ? [] : classCalendar}
            buttonText={{
              today: "Today",
              month: "Monthly",
              week: "Weekly",
              day: "Daily",
            }}
            headerToolbar={{
              start: "title",
              end: "prev,next timeGridDay,timeGridWeek,dayGridMonth",
            }}
            footerToolbar={{
              end: "today",
            }}
            dayCellContent={renderDayCell}
            dayHeaderContent={renderDayHeader}
            slotLabelContent={renderSlotLabel}
            eventContent={renderEvent}
            eventDidMount={handleEventDidMount}
            viewDidMount={handleChangeView}
            datesSet={handleChangeDate}
          />
          <div id="event-popover-overlay">
            <div
              id="event-popover-container"
              className={isLoadingClasses ? "loading" : ""}
            >
              {!isLoadingClasses && renderEventPopovers(classCalendar)}
            </div>
          </div>
        </div>
      </Panel>
    </section>
  )
}

export default ClassCalendar
